import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer, morphSectionHydrationTarget } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import {
  createViewEventElement,
  CartLinesUpdateEvent,
  CartDiscountUpdateEvent,
  StandardEvents,
} from '@shopify/events';
import { cartPerformance } from '@theme/performance';
import { formatMoney } from '@theme/money-formatting';

/** Theme-private signal so `<cart-drawer-component>` can restore cart-actions to home before a section morph. */
const CART_DRAWER_PRE_MORPH_EVENT = 'cart-drawer:pre-morph';

/** Theme-private signal so `<cart-drawer-component>` can re-sync empty-state markup without a section fetch. */
const CART_DRAWER_EMPTY_STATE_SYNC_EVENT = 'cart-drawer:sync-empty-state';

/** @typedef {import('./utilities').TextComponent} TextComponent */
/** @typedef {'change' | 'clear'} CartUpdateAction */
/**
 * @typedef {object} UpdateQuantityConfig
 * @property {number} [line]
 * @property {string} [key]
 * @property {number} quantity
 * @property {CartUpdateAction} action
 */

/**
 * Cart line items UI. Extends createViewEventElement for `shopify:cart:view`
 * (drawer: `view-event-trigger="dialog"`; cart page: default connect).
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /** @type {number} Tracks in-flight add-to-cart requests for empty cart page state. */
  #cartAddPendingCount = 0;

  /** @type {HTMLTemplateElement | null} */
  #cartItemSkeletonRowTemplate = null;

  /** @type {HTMLTemplateElement | null} */
  #cartItemsSkeletonShellTemplate = null;

  /** @type {Promise<void>} Serializes cart mutations that depend on line order. */
  #cartMutationQueue = Promise.resolve();

  /** @type {Set<string>} Line item keys already removed optimistically by this component. */
  #optimisticallyRemovedKeys = new Set();

  connectedCallback() {
    super.connectedCallback();

    this.#cartItemSkeletonRowTemplate ??= this.querySelector('template[data-cart-item-skeleton-row-template]');
    this.#cartItemsSkeletonShellTemplate ??= this.querySelector('template[data-cart-items-skeleton-shell-template]');

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      /** @type {EventListener} */ (this.#handleCartLinesUpdate)
    );
    document.addEventListener(
      StandardEvents.cartDiscountUpdate,
      /** @type {EventListener} */ (this.handleDiscountUpdate)
    );
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      /** @type {EventListener} */ (/** @type {unknown} */ (this.#debouncedOnChange))
    );

    this.#syncPendingEmptyState();
    this.#hydrateCartErrorCopyActions();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      /** @type {EventListener} */ (this.#handleCartLinesUpdate)
    );
    document.removeEventListener(
      StandardEvents.cartDiscountUpdate,
      /** @type {EventListener} */ (this.handleDiscountUpdate)
    );
    document.removeEventListener(
      ThemeEvents.quantitySelectorUpdate,
      /** @type {EventListener} */ (/** @type {unknown} */ (this.#debouncedOnChange))
    );

    this.removeAttribute('data-cart-pending-empty');
    this.removeAttribute('data-cart-add-skeleton');
    this.removeAttribute('aria-busy');
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const quantitySelectorUpdateEvent = /** @type {QuantitySelectorUpdateEvent} */ (event);

    if (!(quantitySelectorUpdateEvent.target instanceof Node) || !this.contains(quantitySelectorUpdateEvent.target)) return;

    const { quantity, cartLine: line } = quantitySelectorUpdateEvent.detail;

    // Cart items require a line number
    if (!line) return;

    const lineItemRow =
      quantitySelectorUpdateEvent.target instanceof HTMLElement
        ? quantitySelectorUpdateEvent.target.closest('.cart-items__table-row[data-key]')
        : null;
    const key = lineItemRow instanceof HTMLTableRowElement ? lineItemRow.dataset.key : undefined;

    if (quantity === 0) {
      if (lineItemRow instanceof HTMLTableRowElement) {
        this.#shimmerQuantitySelector(lineItemRow);
      }

      this.updateQuantity({
        line,
        key,
        quantity,
        action: 'clear',
      });
      return;
    }

    this.updateQuantity({
      line,
      key,
      quantity,
      action: 'change',
    });

    if (!(lineItemRow instanceof HTMLTableRowElement)) return;

    this.#shimmerQuantitySelector(lineItemRow);

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /** @param {HTMLTableRowElement} lineItemRow */
  #shimmerQuantitySelector(lineItemRow) {
    const quantitySelector = lineItemRow.querySelector('cart-quantity-selector-component');
    if (!(quantitySelector instanceof HTMLElement)) return;

    quantitySelector.setAttribute('shimmer', '');
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });
  }

  /**
   * Handles line item removal by stable Shopify line item key.
   * @param {string} key - The line item key.
   */
  onLineItemRemoveByKey(key) {
    this.updateQuantity({
      key,
      quantity: 0,
      action: 'clear',
    });
  }

  /**
   * Optimistically removes the line from the UI and queues the cart mutation.
   * @param {HTMLTableRowElement} cartItemRowToRemove
   * @param {number} line
   * @param {string} [key]
   * @returns {Promise<void>}
   */
  #removeLineItem(cartItemRowToRemove, line, key = cartItemRowToRemove.dataset.key) {
    if (key) {
      this.#optimisticallyRemovedKeys.add(key);
    }

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      this.#moveFocusFromRemovingRow(cartItemRowToRemove);

      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
        this.#clearAddSkeletonRows();
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      if (this.isDrawer) {
        queueMicrotask(() => this.#dispatchDrawerEmptyStateSync(0));
      }

      const update = this.#executeCartQuantityUpdate({
        line,
        key,
        quantity: 0,
        action: 'clear',
      });

      if (key) {
        update.finally(() => this.#optimisticallyRemovedKeys.delete(key));
      }

      return update;
    }

    this.#moveFocusFromRemovingRow(cartItemRowToRemove);

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      this.#disableInteractiveControls(row);

      const remove = () => {
        row.remove();
        if (row === cartItemRowToRemove && key) {
          this.#optimisticallyRemovedKeys.delete(key);
        }
      };

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });

    this.#refreshCartLineIndices();

    return this.#executeCartQuantityUpdate({
      line,
      key,
      quantity: 0,
      action: 'clear',
    });
  }

  /** @param {HTMLElement} container */
  #disableInteractiveControls(container) {
    const controls = container.querySelectorAll('button, input, select, textarea');
    controls.forEach((control) => {
      if (
        control instanceof HTMLButtonElement ||
        control instanceof HTMLInputElement ||
        control instanceof HTMLSelectElement ||
        control instanceof HTMLTextAreaElement
      ) {
        control.disabled = true;
      }
    });
  }

  /**
   * Moves focus out of a row before it is disabled or removed.
   * @param {HTMLTableRowElement} removedRow
   */
  #moveFocusFromRemovingRow(removedRow) {
    const activeElement = document.activeElement;
    if (!(activeElement instanceof HTMLElement) || !removedRow.contains(activeElement)) return;

    const focusTarget = this.#findFocusTargetAfterRowRemoval(removedRow);
    if (!focusTarget) return;

    requestAnimationFrame(() => {
      focusTarget.focus();
    });
  }

  /**
   * @param {HTMLTableRowElement} removedRow
   * @returns {HTMLElement | null}
   */
  #findFocusTargetAfterRowRemoval(removedRow) {
    const removedIndex = this.refs.cartItemRows.indexOf(removedRow);

    if (removedIndex !== -1) {
      for (let index = removedIndex + 1; index < this.refs.cartItemRows.length; index++) {
        const focusTarget = this.#getRowFocusTarget(this.refs.cartItemRows[index], removedRow);
        if (focusTarget) return focusTarget;
      }

      for (let index = removedIndex - 1; index >= 0; index--) {
        const focusTarget = this.#getRowFocusTarget(this.refs.cartItemRows[index], removedRow);
        if (focusTarget) return focusTarget;
      }
    }

    const drawerCloseButton = this.closest('cart-drawer-component')?.querySelector('.theme-drawer__close-button');
    if (drawerCloseButton instanceof HTMLElement) return drawerCloseButton;

    const heading = this.querySelector('#cart-drawer-heading, .cart-drawer__heading, h2');
    if (heading instanceof HTMLElement) {
      if (!heading.hasAttribute('tabindex')) {
        heading.setAttribute('tabindex', '-1');
      }

      return heading;
    }

    return null;
  }

  /**
   * @param {HTMLTableRowElement | undefined} row
   * @param {HTMLTableRowElement} removedRow
   * @returns {HTMLElement | null}
   */
  #getRowFocusTarget(row, removedRow) {
    if (!row || row === removedRow || row.classList.contains('removing')) return null;

    const focusTarget = row.querySelector(
      'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    return focusTarget instanceof HTMLElement ? focusTarget : null;
  }

  /**
   * Updates the quantity.
    * @param {UpdateQuantityConfig} config - The config.
   */
  updateQuantity(config) {
    this.#markCartMutationStart();

    const runUpdate = () =>
      config.action === 'clear' ? this.#optimisticallyClearLineItem(config) : this.#executeCartQuantityUpdate(config);
    this.#cartMutationQueue = this.#cartMutationQueue.then(runUpdate, runUpdate);
  }

  #markCartMutationStart() {
    const win = /** @type {Window & { __themeCartMutationVersion?: number }} */ (window);
    win.__themeCartMutationVersion = (win.__themeCartMutationVersion ?? 0) + 1;
  }

  /**
   * Resolves the current visible row before animating and syncing a line removal.
   * @param {UpdateQuantityConfig} config
   * @returns {Promise<void>}
   */
  #optimisticallyClearLineItem(config) {
    const cartItemRowToRemove = config.key
      ? this.#getVisibleCartItemRowByKey(config.key)
      : this.#getVisibleCartItemRowByLine(config.line ?? 0);

    if (!cartItemRowToRemove) {
      if (config.key && this.#optimisticallyRemovedKeys.has(config.key)) {
        return Promise.resolve();
      }

      if (config.key || config.line) {
        return this.#executeCartQuantityUpdate({
          line: config.line,
          key: config.key,
          quantity: 0,
          action: 'clear',
        });
      }

      return Promise.resolve();
    }

    const line = this.#getVisibleCartLineForRow(cartItemRowToRemove);
    if (!line) return Promise.resolve();

    return this.#removeLineItem(cartItemRowToRemove, line, config.key ?? cartItemRowToRemove.dataset.key);
  }

  /**
   * Performs the cart change network request and DOM updates.
   * @param {UpdateQuantityConfig} config - The config.
   */
  #executeCartQuantityUpdate(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const useLightweightUpdate = config.action === 'clear';
    const showPendingState = !useLightweightUpdate;

    // Dispatch the standard event before the request so the drawer/peers can react (pending bar,
    // layout) and settle once this component finishes updating.
    const deferred = CartLinesUpdateEvent.createPromise();
    let settled = false;
    /**
     * @param {any} parsed
     * @param {{ lightweight?: boolean, didError?: boolean, sections?: Record<string, string> }} [opts]
     */
    const settle = (parsed, opts = {}) => {
      if (settled) return;
      settled = true;
      this.#resolveLinesUpdate(deferred, parsed, opts);
    };
    this.#dispatchCartLinesUpdate(config, deferred.promise);

    if (showPendingState) {
      this.#disableCartItems();
    }

    const { quantity } = config;
    const line = config.line ?? 0;
    const { cartTotal } = this.refs;

    /** @type {{ line?: number, id?: string, quantity: number, sections?: string, sections_url?: string }} */
    const requestPayload =
      config.key
        ? {
            id: config.key,
            quantity,
          }
        : {
            line,
            quantity,
          };

    if (!useLightweightUpdate) {
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set([this.sectionId]);
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      requestPayload.sections = Array.from(sectionsToUpdate).join(',');
      requestPayload.sections_url = window.location.pathname;
    }

    const body = JSON.stringify(requestPayload);

    if (showPendingState) {
      cartTotal?.shimmer();
    }

    return fetch(this.#getCartChangeUrl(), fetchConfig('json', { body }))
      .then(async (response) => {
        const responseText = await response.text();

        if (!this.#isJsonResponse(response)) {
          throw new Error(`Unexpected cart response (status: ${response.status})`);
        }

        return JSON.parse(responseText);
      })
      .then((parsedResponseText) => {
        if (showPendingState) {
          resetShimmer(this);
        }

        const cartErrorMessage = parsedResponseText.errors ?? parsedResponseText.description;
        if (cartErrorMessage) {
          if (useLightweightUpdate) {
            if (config.key) {
              this.#optimisticallyRemovedKeys.delete(config.key);
            }

            settle(null, { didError: true });
            return sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => undefined);
          }

          this.#handleCartError(line, {
            ...parsedResponseText,
            errors: cartErrorMessage,
          });
          settle(null, { didError: true });
          return;
        }

        if (useLightweightUpdate) {
          const newCartItemCount =
            typeof parsedResponseText.item_count === 'number' ? parsedResponseText.item_count : 0;

          const parsedCartItems = /** @type {Array<{key?: string}> | undefined} */ (parsedResponseText.items);
          const keyStillInCart =
            config.key &&
            Array.isArray(parsedCartItems) &&
            parsedCartItems.some((item) => item.key === config.key);

          if (keyStillInCart && config.key) {
            this.#optimisticallyRemovedKeys.delete(config.key);
            settle(parsedResponseText, { lightweight: true });
            return sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => undefined);
          }

          this.#applyLightweightCartUpdate(parsedResponseText);

          settle(parsedResponseText, { lightweight: true });

          if (this.isDrawer) {
            queueMicrotask(() => this.#dispatchDrawerEmptyStateSync(newCartItemCount));
          }

          return;
        }

        const sectionMarkup = parsedResponseText.sections?.[this.sectionId];
        if (!sectionMarkup) {
          console.warn(`Cart response did not include section ${this.sectionId}`);
          settle(parsedResponseText, { sections: parsedResponseText.sections });
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(sectionMarkup, 'text/html');

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        settle(parsedResponseText, { sections: parsedResponseText.sections });

        this.#morphCartSection(this.sectionId, sectionMarkup, newCartItemCount);

        this.#updateCartQuantitySelectorButtonStates();
        this.#hydrateCartErrorCopyActions();
      })
      .catch((error) => {
        console.error(error);
        if (config.key) {
          this.#optimisticallyRemovedKeys.delete(config.key);
        }

        settle(null, { didError: true });
        return sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => undefined);
      })
      .finally(() => {
        if (!settled) settle(null, { didError: true });
        if (showPendingState) {
          resetShimmer(this);
          this.#enableCartItems();
        }
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Dispatches a standard `CartLinesUpdateEvent` for this component's own quantity change/removal.
   * The event is dispatched on `this`, so this component skips it while peers and the drawer react.
   * @param {UpdateQuantityConfig} config
   * @param {Promise<any>} promise
   */
  #dispatchCartLinesUpdate(config, promise) {
    const action = config.action === 'clear' ? 'remove' : 'update';
    const id = config.key ?? (config.line != null ? String(config.line) : '');

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action,
        context: this.isDrawer ? 'dialog' : 'cart',
        lines: [{ id, quantity: config.quantity }],
        promise,
      })
    );
  }

  /**
   * Resolves the deferred `CartLinesUpdateEvent` promise with the cart summary plus theme-specific
   * detail (raw AJAX cart, section HTML, lightweight flag) so consumers can reconcile.
   * @param {ReturnType<typeof CartLinesUpdateEvent.createPromise>} deferred
   * @param {any} parsed - The parsed AJAX cart response (or null on error).
   * @param {{ lightweight?: boolean, didError?: boolean, sections?: Record<string, string> }} [opts]
   */
  #resolveLinesUpdate(deferred, parsed, { lightweight = false, didError = false, sections } = {}) {
    const cartData = /** @type {{ item_count?: number, items?: unknown[] } | null} */ (parsed);

    deferred.resolve({
      cart: !didError && cartData ? CartLinesUpdateEvent.createCartFromAjaxResponse(parsed) : null,
      detail: {
        resource: cartData ?? {},
        items: cartData?.items,
        sections: sections ?? /** @type {any} */ (cartData)?.sections,
        lightweight,
        didError,
        source: 'cart-items-component',
        sourceId: this.sectionId,
        itemCount: typeof cartData?.item_count === 'number' ? cartData.item_count : undefined,
      },
    });
  }

  /**
   * @param {Response} response
   * @returns {boolean}
   */
  #isJsonResponse(response) {
    const contentType = response.headers.get('content-type');
    return (
      contentType?.includes('application/json') === true ||
      contentType?.includes('text/javascript') === true
    );
  }

  /**
   * Ensures quantity updates use Shopify's Ajax cart endpoint.
   * @returns {string}
   */
  #getCartChangeUrl() {
    const cartChangeUrl = Theme.routes?.cart_change_url ?? '/cart/change';
    const [rawPath = '/cart/change', ...queryParts] = cartChangeUrl.split('?');
    const path = rawPath || '/cart/change';
    const normalizedPath = path.endsWith('.js') ? path : `${path}.js`;
    const query = queryParts.join('?');

    return query ? `${normalizedPath}?${query}` : normalizedPath;
  }

  /**
   * Handles a standard cart discount update by re-rendering this section once the discount settles.
   * @param {CartDiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        // The discount component that owns this section performs its own morph.
        // A second fetch here races that morph and can erase focused form state.
        if (detail?.sourceId && detail?.sectionId === this.sectionId) {
          this.#syncPendingEmptyState();
          return;
        }

        const sectionHtml = detail?.sections?.[this.sectionId];
        if (typeof sectionHtml === 'string' && sectionHtml.length > 0) {
          void morphSection(this.sectionId, sectionHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          void sectionRenderer.renderSection(this.sectionId, { cache: false });
        }
        this.#syncPendingEmptyState();
      })
      .catch(() => {});
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
    * @param {number} [parsedResponseText.status] - The status code.
    * @param {string} [parsedResponseText.description] - A more descriptive error message.
   */
  #handleCartError = (line, parsedResponseText) => {
    const row = this.#getVisibleCartItemRowByLine(line);
    if (!row) throw new Error('Cart item row not found');

    const quantityInput = row.querySelector('cart-quantity-selector-component input');
    if (!(quantityInput instanceof HTMLInputElement)) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemErrorContainer = row.querySelector('[ref^="cartItemErrorContainer-"]');
    const cartItemError = row.querySelector('[ref^="cartItemError-"]');

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    const errorMessage = parsedResponseText.errors ?? parsedResponseText.description ?? 'Unable to update cart.';

    cartItemError.textContent = errorMessage;
    cartItemError.dataset.cartErrorMessage = errorMessage;
    cartItemError.classList.remove('visually-hidden');
    cartItemErrorContainer.classList.remove('hidden');
    this.#attachCartErrorCopyAction(cartItemErrorContainer, line, parsedResponseText);
  };

  /**
   * Attaches copy-details behavior to the existing cart error icon.
   * @param {HTMLElement} cartItemErrorContainer
   * @param {number} line
   * @param {{errors?: string, status?: number, description?: string}} parsedResponseText
   */
  #attachCartErrorCopyAction(cartItemErrorContainer, line, parsedResponseText) {
    const errorMessage = parsedResponseText.errors ?? parsedResponseText.description ?? 'Unable to update cart.';
    const cartItemError = cartItemErrorContainer.querySelector('[ref^="cartItemError-"]');
    if (!(cartItemError instanceof HTMLElement)) return;

    cartItemError.textContent = errorMessage;
    cartItemError.dataset.cartErrorMessage = errorMessage;

    const iconWrapper = cartItemErrorContainer.querySelector('.cart-item__error .svg-wrapper');
    if (!(iconWrapper instanceof HTMLElement)) return;

    const copyLabel = 'Copy cart error details';
    const copiedLabel = 'Copied cart error details';
    const failedLabel = 'Unable to copy cart error details';
    const unavailableLabel = 'Clipboard copy unavailable';

    const errorReport = this.#buildCartErrorReport(line, parsedResponseText);

    iconWrapper.classList.add('cart-item__error-copy-trigger');
    iconWrapper.setAttribute('role', 'button');
    iconWrapper.setAttribute('tabindex', '0');
    iconWrapper.setAttribute('aria-label', copyLabel);
    iconWrapper.setAttribute('title', copyLabel);
    iconWrapper.setAttribute('data-cart-error-copy-trigger', '');

    /** @param {string} statusLabel */
    const setIconCopyStatus = (statusLabel) => {
      iconWrapper.setAttribute('aria-label', statusLabel);
      iconWrapper.setAttribute('title', statusLabel);
    };

    const copyErrorDetails = async () => {
      if (!navigator.clipboard?.writeText) {
        setIconCopyStatus(unavailableLabel);
        return;
      }

      try {
        await navigator.clipboard.writeText(errorReport);
        setIconCopyStatus(copiedLabel);
      } catch {
        setIconCopyStatus(failedLabel);
      }

      setTimeout(() => {
        setIconCopyStatus(copyLabel);
      }, 3000);
    };

    iconWrapper.onclick = () => {
      void copyErrorDetails();
    };

    iconWrapper.onkeydown = (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;

      event.preventDefault();
      void copyErrorDetails();
    };
  }

  /**
   * Builds a support-friendly error payload for customer reporting.
   * @param {number} line
   * @param {{errors?: string, status?: number, description?: string}} parsedResponseText
   * @returns {string}
   */
  #buildCartErrorReport(line, parsedResponseText) {
    const errorMessage = parsedResponseText.errors ?? parsedResponseText.description ?? 'Unknown cart error';

    return [
      'Cart update failed',
      `Time: ${new Date().toISOString()}`,
      `Page: ${window.location.pathname}`,
      `Section: ${this.sectionId}`,
      `Cart line: ${line}`,
      `Status: ${parsedResponseText.status ?? 'unknown'}`,
      `Message: ${errorMessage}`,
    ].join('\n');
  }

  /**
   * Handles the standard cart lines update. Adds render optimistic skeleton rows synchronously and
   * reconcile on settle; own quantity updates are handled inline, so only peer updates reconcile here.
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    if (event.action === 'add') {
      // Optimistic skeleton rows synchronously, before the promise settles (replaces CartAddPendingEvent).
      if (this.#shouldShowAddSkeleton()) {
        this.#cartAddPendingCount++;
        const lineCount = Array.isArray(event.lines) ? event.lines.length : 1;
        this.#appendAddSkeletonRows(lineCount > 0 ? lineCount : 1);
        this.#syncPendingEmptyState();
      }

      event.promise
        ?.then((result) => this.#applyCartAddResult(/** @type {any} */ (result)?.detail ?? {}))
        .catch(() => this.#applyCartAddResult({ didError: true }));
      return;
    }

    // Our own quantity change/removal is handled inline in #executeCartQuantityUpdate.
    if (event.target === this) return;

    event.promise
      ?.then((result) => this.#applyPeerCartUpdate(/** @type {any} */ (result)?.detail ?? {}))
      .catch(() => {});
  };

  /**
   * Reconciles this component after an add-to-cart settles, then clears the optimistic skeleton.
   * @param {any} detail
   */
  #applyCartAddResult(detail = {}) {
    if (!detail.didError) {
      if (detail.lightweight === true) {
        this.#applyLightweightCartUpdate(/** @type {object} */ (detail.resource ?? {}), {
          syncRows: false,
          forceSectionRefresh: true,
        });
      } else {
        const cartItemsHtml = detail.sections?.[this.sectionId];
        if (cartItemsHtml) {
          const itemCount = detail.resource?.item_count ?? detail.itemCount;
          this.#morphCartSection(this.sectionId, cartItemsHtml, itemCount);
          this.#updateCartQuantitySelectorButtonStates();
          this.#hydrateCartErrorCopyActions();
          this.#clearAddSkeletonRows();
        } else {
          void sectionRenderer.renderSection(this.sectionId, { cache: false }).then((sectionHTML) => {
            this.#finalizeLightweightSectionRefresh();
            if (!this.isDrawer) return;
            queueMicrotask(() => {
              this.#dispatchDrawerEmptyStateSync(this.#parseCartItemCountFromSectionHtml(sectionHTML));
            });
          });
        }
      }
    }

    this.#cartAddPendingCount = Math.max(0, this.#cartAddPendingCount - 1);

    const shouldDeferSkeletonClear = detail.lightweight === true || (detail.didError !== true && !detail.sections);

    if (this.#cartAddPendingCount === 0 && !shouldDeferSkeletonClear) {
      this.#clearAddSkeletonRows();
    }

    this.#syncPendingEmptyState();
  }

  /**
   * Reconciles this component after a peer component's cart quantity change/removal settles.
   * @param {any} detail
   */
  #applyPeerCartUpdate(detail = {}) {
    if (detail.didError) {
      this.#syncPendingEmptyState();
      return;
    }

    if (detail.lightweight === true) {
      this.#applyLightweightCartUpdate(/** @type {object} */ (detail.resource ?? {}), { syncRows: true });
      this.#syncPendingEmptyState();
      return;
    }

    const cartItemsHtml = detail.sections?.[this.sectionId];
    if (cartItemsHtml) {
      const itemCount = detail.resource?.item_count ?? detail.itemCount;
      this.#morphCartSection(this.sectionId, cartItemsHtml, itemCount);
      this.#updateCartQuantitySelectorButtonStates();
      this.#hydrateCartErrorCopyActions();
      this.#clearAddSkeletonRows();
    } else {
      void sectionRenderer.renderSection(this.sectionId, { cache: false }).then((sectionHTML) => {
        if (!this.isDrawer) return;
        queueMicrotask(() => {
          this.#dispatchDrawerEmptyStateSync(this.#parseCartItemCountFromSectionHtml(sectionHTML));
        });
      });
    }

    this.#syncPendingEmptyState();
  }

  /** @returns {boolean} */
  #shouldShowAddSkeleton() {
    if (this.isDrawer) {
      const drawerComponent = this.closest('cart-drawer-component');
      const dialog = drawerComponent?.querySelector('dialog');
      const isOpen = dialog instanceof HTMLDialogElement && dialog.open;
      const willAutoOpen = drawerComponent?.hasAttribute('auto-open') === true;
      return isOpen || willAutoOpen;
    }

    return this.querySelector('.cart-page') instanceof HTMLElement;
  };

  /**
   * @param {number} count
   */
  #appendAddSkeletonRows(count) {
    const tbody = this.#getOrCreateCartItemsTableBody();
    if (!tbody) return;

    const fragment = document.createDocumentFragment();
    for (let i = 0; i < count; i++) {
      const row = this.#cloneAddSkeletonRow();
      if (row) fragment.append(row);
    }

    if (!fragment.childElementCount) return;

    tbody.insertBefore(fragment, tbody.firstChild);

    this.setAttribute('data-cart-add-skeleton', '');
    this.setAttribute('aria-busy', 'true');
  }

  #clearAddSkeletonRows() {
    for (const row of this.querySelectorAll('[data-cart-item-skeleton]')) {
      row.remove();
    }

    const shell = this.querySelector('[data-cart-items-skeleton-shell]');
    if (shell instanceof HTMLElement) {
      const hasRows = shell.querySelector('.cart-items__table tbody tr');
      if (!hasRows) {
        shell.remove();
      }
    }

    if (!this.querySelector('[data-cart-item-skeleton]')) {
      this.removeAttribute('data-cart-add-skeleton');
      this.removeAttribute('aria-busy');
    }
  }

  /** @returns {HTMLTableSectionElement | null} */
  #getOrCreateCartItemsTableBody() {
    const existingBody = this.querySelector('.cart-items__table tbody');
    if (existingBody instanceof HTMLTableSectionElement) {
      return existingBody;
    }

    const host =
      this.querySelector('.cart-drawer__items') ||
      this.querySelector('.cart-page__items') ||
      this.querySelector('.cart-items__wrapper');

    const shellTemplate = this.#cartItemsSkeletonShellTemplate;
    if (!(host instanceof HTMLElement) || !(shellTemplate instanceof HTMLTemplateElement)) {
      return null;
    }

    const shell = shellTemplate.content.firstElementChild;
    if (!(shell instanceof HTMLElement)) return null;

    host.append(shell.cloneNode(true));

    const tbody = host.querySelector('[data-cart-items-skeleton-shell] tbody');
    return tbody instanceof HTMLTableSectionElement ? tbody : null;
  }

  /** @returns {HTMLTableRowElement | null} */
  #cloneAddSkeletonRow() {
    const template = this.#cartItemSkeletonRowTemplate;
    if (!(template instanceof HTMLTemplateElement)) return null;

    const row = template.content.firstElementChild;
    if (!(row instanceof HTMLTableRowElement)) return null;

    return /** @type {HTMLTableRowElement} */ (row.cloneNode(true));
  }

  #syncPendingEmptyState() {
    if (this.isDrawer) return;

    const isEmptyCartPage = this.querySelector('.cart-page--empty') instanceof HTMLElement;
    const shouldShowPendingState = isEmptyCartPage && this.#cartAddPendingCount > 0;

    if (shouldShowPendingState) {
      this.setAttribute('data-cart-pending-empty', '');
      return;
    }

    this.removeAttribute('data-cart-pending-empty');
  }

  /**
   * Morph cart section markup, fully replacing drawer inner content when leaving empty state.
   * @param {string} sectionId
   * @param {string} html
   * @param {number | undefined} [itemCount]
   */
  #morphCartSection(sectionId, html, itemCount) {
    // Let the drawer restore cart-actions to its home position before the section morph replaces it.
    this.dispatchEvent(new CustomEvent(CART_DRAWER_PRE_MORPH_EVENT, { bubbles: true }));

    if (!this.isDrawer || !this.#isDrawerEmpty()) {
      morphSection(sectionId, html, { mode: this.isDrawer ? 'hydration' : 'full' });
    } else if (!morphSectionHydrationTarget(sectionId, html, 'cart-drawer-inner')) {
      morphSection(sectionId, html, { mode: 'hydration' });
    }

    if (this.isDrawer) {
      queueMicrotask(() => {
        const resolvedItemCount =
          typeof itemCount === 'number' ? itemCount : this.#parseCartItemCountFromSectionHtml(html);
        this.#dispatchDrawerEmptyStateSync(resolvedItemCount);
      });
    }
  }

  /**
   * @param {string} html
   * @returns {number | undefined}
   */
  #parseCartItemCountFromSectionHtml(html) {
    const parsedCount = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('[ref="cartItemCount"]')
      ?.textContent;

    if (parsedCount == null || parsedCount === '') return undefined;

    const itemCount = parseInt(parsedCount, 10);
    return Number.isFinite(itemCount) ? itemCount : undefined;
  }

  /**
   * @param {number | undefined} itemCount
   */
  #dispatchDrawerEmptyStateSync(itemCount) {
    this.closest('cart-drawer-component')?.dispatchEvent(
      new CustomEvent(CART_DRAWER_EMPTY_STATE_SYNC_EVENT, {
        detail: { itemCount },
      })
    );
  }

  /** @returns {boolean} */
  #isDrawerEmpty() {
    const dialog = this.closest('cart-drawer-component')?.closest('dialog');
    return dialog instanceof HTMLElement && Boolean(dialog.querySelector('#cart-drawer-heading-empty'));
  }

  /**
   * Ensures rendered cart error rows expose inline copy actions.
   */
  #hydrateCartErrorCopyActions() {
    const errorContainers = this.querySelectorAll('[ref^="cartItemErrorContainer-"]');

    for (const container of errorContainers) {
      if (!(container instanceof HTMLElement) || container.classList.contains('hidden')) continue;

      const lineMatch = container.getAttribute('ref')?.match(/cartItemErrorContainer-(\d+)/);
      const lineToken = lineMatch?.[1];
      if (!lineToken) continue;

      const line = parseInt(lineToken, 10);
      if (!Number.isFinite(line)) continue;

      const errorElement = this.refs[`cartItemError-${line}`];
      if (!(errorElement instanceof HTMLElement)) continue;

      errorElement.classList.remove('visually-hidden');
      const errorMessage =
        errorElement.dataset.cartErrorMessage ||
        errorElement.textContent?.trim() ||
        container.textContent?.trim() ||
        'Unable to update cart.';

        this.#attachCartErrorCopyAction(container, line, { errors: errorMessage });
    }
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Applies cart JSON updates after a lightweight cart mutation (no section HTML).
   * @param {{ item_count?: number, total_price?: number, currency?: string, items?: Array<{key?: string, quantity?: number}> }} cart
   * @param {{ syncRows?: boolean, forceSectionRefresh?: boolean }} [options]
   */
  #applyLightweightCartUpdate(cart, { syncRows = true, forceSectionRefresh = false } = {}) {
    const itemCount = typeof cart.item_count === 'number' ? cart.item_count : 0;
    const onComplete = this.querySelector('[data-cart-item-skeleton]')
      ? () => this.#finalizeLightweightSectionRefresh()
      : undefined;

    if (syncRows) {
      this.#syncLightweightCartRows(cart);
    }

    if (itemCount > 0 && !this.querySelector('.cart-form')) {
      void sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => {
        onComplete?.();
      });
      return;
    }

    if (itemCount === 0) {
      if (this.#replaceWithLightweightEmptyDrawer()) {
        onComplete?.();
        return;
      }

      if (!this.isDrawer) {
        void sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => {
          onComplete?.();
        });
        return;
      }
    }

    const countElement = this.querySelector('[ref="cartItemCount"]');
    if (countElement) {
      countElement.textContent = String(itemCount);
    }

    if (typeof cart.total_price === 'number') {
      const formattedTotal = this.#formatCartMoney(cart.total_price, cart.currency);
      const { cartTotal } = this.refs;

      if (cartTotal instanceof HTMLElement) {
        cartTotal.setAttribute('value', formattedTotal);
        cartTotal.textContent = formattedTotal;
      }

      const captionTotal = this.querySelector('.cart-items__table caption span');
      if (captionTotal instanceof HTMLElement) {
        captionTotal.textContent = formattedTotal;
      }
    }

    const drawerBadge = this.closest('cart-drawer-component')?.querySelector('.theme-drawer__badge');

    if (drawerBadge instanceof HTMLElement) {
      drawerBadge.textContent = itemCount > 99 ? '99+' : String(itemCount);
      drawerBadge.classList.toggle('visually-hidden', itemCount === 0);
      drawerBadge.toggleAttribute('data-maintain-ratio', itemCount <= 99);
    }

    this.#refreshLightweightSectionInBackground(onComplete, { force: forceSectionRefresh });
  }

  #finalizeLightweightSectionRefresh() {
    this.#updateCartQuantitySelectorButtonStates();
    this.#hydrateCartErrorCopyActions();

    if (this.#cartAddPendingCount === 0) {
      this.#clearAddSkeletonRows();
    }
  }

  /**
   * Reconciles existing row markup after a lightweight cart JSON update.
   * @param {{items?: Array<{key?: string, quantity?: number}>}} cart
   */
  #syncLightweightCartRows(cart) {
    if (!Array.isArray(cart.items)) return;

    const cartItemsByKey = new Map();
    cart.items.forEach((item, index) => {
      if (!item.key) return;

      cartItemsByKey.set(item.key, {
        ...item,
        line: index + 1,
      });
    });

    for (const row of this.querySelectorAll('.cart-items__table-row[data-key]')) {
      if (!(row instanceof HTMLTableRowElement)) continue;

      const key = row.dataset.key;
      const item = key ? cartItemsByKey.get(key) : undefined;

      if (!item) {
        if (row.classList.contains('removing') || (key && this.#optimisticallyRemovedKeys.has(key))) {
          continue;
        }

        row.remove();
        continue;
      }

      const quantityInput = row.querySelector('cart-quantity-selector-component input[data-cart-line]');
      if (quantityInput instanceof HTMLInputElement) {
        quantityInput.dataset.cartLine = String(item.line);
        if (typeof item.quantity === 'number') {
          quantityInput.value = String(item.quantity);
        }
      }
    }

    this.#refreshCartLineIndices();
  }

  /**
   * Re-indexes visible cart quantity inputs after optimistic row removals.
   */
  #refreshCartLineIndices() {
    const rows = this.querySelectorAll('.cart-items__table-row[data-key]:not(.removing)');

    rows.forEach((row, index) => {
      const quantityInput = row.querySelector('cart-quantity-selector-component input[data-cart-line]');

      if (quantityInput instanceof HTMLInputElement) {
        quantityInput.dataset.cartLine = String(index + 1);
      }
    });
  }

  /**
   * Gets a non-removing cart row by the current visible cart line number.
   * @param {number} line
   * @returns {HTMLTableRowElement | null}
   */
  #getVisibleCartItemRowByLine(line) {
    const visibleRows = this.refs.cartItemRows.filter((row) => !row.classList.contains('removing'));
    return visibleRows[line - 1] ?? null;
  }

  /**
   * Gets a non-removing cart row by Shopify line item key.
   * @param {string} key
   * @returns {HTMLTableRowElement | null}
   */
  #getVisibleCartItemRowByKey(key) {
    return this.refs.cartItemRows.find((row) => !row.classList.contains('removing') && row.dataset.key === key) ?? null;
  }

  /**
   * Gets the current visible line number for a cart row.
   * @param {HTMLTableRowElement} row
   * @returns {number | null}
   */
  #getVisibleCartLineForRow(row) {
    const visibleRows = this.refs.cartItemRows.filter((cartItemRow) => !cartItemRow.classList.contains('removing'));
    const index = visibleRows.indexOf(row);

    return index === -1 ? null : index + 1;
  }

  /**
   * Swaps a peer drawer into empty state after a lightweight cart clear.
   * @returns {boolean}
   */
  #replaceWithLightweightEmptyDrawer() {
    if (!this.isDrawer || this.#isDrawerEmpty()) return false;

    const template = document.getElementById('empty-cart-template');
    if (!(template instanceof HTMLTemplateElement)) return false;

    const clone = document.importNode(template.content, true);

    startViewTransition(() => {
      this.replaceChildren(clone);
      this.#clearAddSkeletonRows();
    }, ['empty-cart-drawer']);

    queueMicrotask(() => this.#dispatchDrawerEmptyStateSync(0));
    return true;
  }

  /**
   * Refreshes server-rendered summary blocks without blocking optimistic updates.
   * @param {(() => void) | undefined} [onComplete]
   * @param {{ force?: boolean }} [options]
   */
  #refreshLightweightSectionInBackground(onComplete, { force = false } = {}) {
    if (!force && this.isDrawer && !this.#shouldRefreshDrawerSection()) {
      onComplete?.();
      return;
    }

    const refresh = () =>
      sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => {
        onComplete?.();
      });

    this.#cartMutationQueue = this.#cartMutationQueue.then(refresh, refresh);
  }

  /** @returns {boolean} Whether a closed drawer should still receive a background section refresh. */
  #shouldRefreshDrawerSection() {
    const drawerComponent = this.closest('cart-drawer-component');
    const dialog = drawerComponent?.querySelector('dialog');
    const isOpen = dialog instanceof HTMLDialogElement && dialog.open;
    const willAutoOpen = drawerComponent?.hasAttribute('auto-open') === true;

    return isOpen || willAutoOpen;
  }

  /**
   * @param {number} totalPrice
   * @param {string} [currency]
   * @returns {string}
   */
  #formatCartMoney(totalPrice, currency) {
    const moneyFormat = /** @type {{ money_format?: string } | undefined} */ (window.Shopify)?.money_format;
    const currencyCode = currency || 'USD';

    if (moneyFormat) {
      return formatMoney(totalPrice, moneyFormat, currencyCode);
    }

    return (totalPrice / 100).toFixed(2);
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
